import React, { useRef, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth } from "../contexts/firebase";
import { GiftIcon } from "@heroicons/react/solid";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../contexts/firebase";

export default function Login() {
  const navigate = useNavigate();
  const emailRef = useRef();
  const passwordRef = useRef();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [toast, setToast] = useState({ message: "", type: "" });


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is already logged in, redirect them
        navigate("/dashboard"); // or "/generate" or any other page you want
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const backgroundImage = isMobile
    ? "https://geneartai.com/images/mobile1.png"
    : "https://geneartai.com/images/image1.png";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(
        auth,
        emailRef.current.value,
        passwordRef.current.value
      );
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      setError("Login failed: Incorrect email or password.");
      emailRef.current.value = "";
      passwordRef.current.value = "";
    } finally {
      setLoading(false);
    }
  };


  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user already exists in Firestore
      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        await setDoc(userRef, {
          email: user.email,
          name: user.displayName,
          credits: 20,
          createdAt: serverTimestamp(),
        });
      }

      setToast({ message: "Signed in successfully 🎉", type: "success" });
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 1000);
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      setToast({ message: "Google Sign-In failed. Please try again.", type: "error" });
    }
  };


  return (
    <div className="relative min-h-screen font-sans text-white overflow-x-hidden">

      {/* Responsive Background */}
      <div
        className="fixed inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: `url('${backgroundImage}')`,
          backgroundAttachment: isMobile ? "scroll" : "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0"></div>
      </div>

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
          duration={4000}
        />
      )}


      {/* Foreground Content */}
      <div className="relative z-10 flex flex-col min-h-screen">

        {/* Main Content */}
        <div className="flex-grow flex items-center justify-center px-4 py-8 pt-20">
          <div className="bg-black/20 backdrop-blur-md p-8 rounded-2xl shadow-2xl w-full max-w-lg">
            {/* 🎁 20 Free Credits button */}
            <button
              onClick={() => navigate("/signup")}
              className="mx-auto flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 font-bold text-xs sm:text-sm md:text-base rounded-full py-2 px-4 text-center mb-6 shadow-md hover:scale-105 transform transition duration-300 animate-bounce focus:outline-none"
            >
              <GiftIcon className="h-5 w-5 text-yellow-900" />
              New here? Sign up and get 20 Credits!
            </button>
            <h2 className="text-3xl font-bold mb-6 text-center text-white">
              Login to GeneArtAI
            </h2>

            {error && (
              <div className="bg-red-100 text-red-700 text-sm text-center rounded-md p-2 mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">Email</label>
                <input
                  ref={emailRef}
                  type="email"
                  required
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-base font-semibold mb-1 text-gray-200">Password</label>
                <input
                  ref={passwordRef}
                  type="password"
                  required
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-base"
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-1.5 rounded-full font-semibold text-base ${loading
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-blue-800 text-white"
                  } transition duration-300 flex justify-center items-center gap-2 shadow-md hover:shadow-lg`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                    <span>Logging in...</span>
                  </>
                ) : (
                  "Login"
                )}
              </button>
            </form>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="mt-4 w-full py-2.5 rounded-full font-semibold text-base bg-white text-black hover:bg-gray-200 transition duration-300 flex justify-center items-center gap-2 shadow-md"
            >
              <img src="/google.png" alt="Google" className="h-5 w-5" />
              Continue with Google
            </button>


            <p className="text-center text-sm text-gray-300 mt-4">
              Don't have an account?{" "}
              <button
                onClick={() => navigate("/signup")}
                className="text-white font-semibold hover:underline"
              >
                Sign up
              </button>
            </p>
            <p className="text-center text-sm mt-4 text-gray-300">
              <Link to="/forget-password" className="underline hover:text-white">
                Forgot your password?
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center text-xs text-gray-300 pb-3">
          <div>© 2025 GeneArtAI — All Rights Reserved.</div>
          <div className="mt-2">
            <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">
              Privacy Policy
            </Link>{" "}
            |{" "}
            <Link to="/terms-of-service" className="underline hover:text-white">
              Terms of Service
            </Link>
          </div>
        </footer>

        {/* Mobile Bottom Ad Spacer */}
        <div className="block sm:hidden h-20" />
      </div>
    </div>
  );
}
